import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowRight,
  Loader2,
  Lock,
  MessageSquare,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { sendOtp, checkOtp } from '@/lib/api';

const trackPixel = (event, data = {}) => {
  if (window.fbq) {
    if (['Lead', 'InitiateCheckout', 'CompleteRegistration'].includes(event)) {
      window.fbq('track', event, data);
    } else {
      window.fbq('trackCustom', event, data);
    }
  }
};
const trackGA = (event, data = {}) => {
  if (window.gtag) window.gtag('event', event, data);
};

const formatPhone = (val) => {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Shared SMS-verify form used across all paid-traffic landing pages.
 * Renders the dark "Start Your Quote" section with name + phone → OTP → /quote redirect.
 *
 * Props:
 *  - sourceLabel: string — used in tracking (e.g. "get_quote", "cost_miami", "no_sales")
 *  - showInDark: bool — default true; when false, renders form on white background
 */
export default function QuoteVerifyForm({ sourceLabel = 'get_quote', showInDark = true }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Honor a `redirect` query param so the gate can preserve the original
  // destination (e.g. /quote?something). Strict allow-list: relative paths
  // beginning with a single "/" only — never a protocol or "//host" — to
  // prevent open-redirect abuse via crafted ad URLs.
  const safeRedirect = (() => {
    const raw = searchParams.get('redirect') || '';
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/quote';
    return raw;
  })();
  // Also surface an "incoming src" so the bounce-from-gate attribution
  // (e.g. src=direct_quote / src=expired_token) survives all the way to
  // the eventual /quote URL and into GHL.
  const incomingSrc = searchParams.get('src') || '';

  const [verifyStep, setVerifyStep] = useState('contact');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSendCode = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error('Please enter your first name.');
      return;
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Please enter a valid 10-digit US phone number.');
      return;
    }
    setSending(true);
    try {
      const resp = await sendOtp(name.trim(), phone);
      setPhoneE164(resp.data.phone || `+1${digits}`);
      setVerifyStep('code');
      setResendCooldown(30);
      trackPixel('InitiateCheckout', { content_name: 'Verify Phone Gate', source: sourceLabel });
      trackGA('verify_code_sent', { event_category: 'verify', event_label: sourceLabel });
      if (resp.data.dev_mode) {
        toast.info('Test mode: check the backend logs for your 6-digit code.');
      } else {
        toast.success(`Code sent to ${resp.data.phone}`);
      }
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === 'string' ? raw : 'Could not send code. Please try again.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || sending) return;
    await handleSendCode();
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault?.();
    const cleaned = code.replace(/\D/g, '');
    if (cleaned.length < 4) {
      toast.error('Enter the code we just texted you.');
      return;
    }
    setVerifying(true);
    try {
      const resp = await checkOtp(phoneE164 || phone, cleaned, name);
      const token = resp.data.token;
      try {
        localStorage.setItem(
          'snapVerified',
          JSON.stringify({ token, name, phone: phoneE164 || phone, ts: Date.now(), source: sourceLabel })
        );
      } catch (_) {
        // ignore
      }
      trackPixel('CompleteRegistration', { content_name: 'Phone Verified', source: sourceLabel });
      trackGA('verify_success', { event_category: 'verify', event_label: sourceLabel });
      toast.success("You're verified — let's get your photos.");
      const params = new URLSearchParams({
        verified: token,
        name,
        phone: phoneE164 || phone,
        src: incomingSrc || sourceLabel,
      });
      // Splice the verified params onto the resolved redirect target
      // (preserving any existing query the redirect already carries).
      const sep = safeRedirect.includes('?') ? '&' : '?';
      navigate(`${safeRedirect}${sep}${params.toString()}`);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === 'string' ? raw : "That code didn't match. Please try again.";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <section
      id="verify-form"
      className={`py-12 sm:py-20 scroll-mt-24 ${
        showInDark ? 'bg-[var(--brand-slate-900)] text-white' : 'bg-[hsl(var(--secondary))]/40'
      }`}
      data-testid="verify-section"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Card className="rounded-2xl bg-white text-[var(--brand-slate-900)] border-0 shadow-2xl">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0">
                <MessageSquare className="h-5 w-5 text-[var(--brand-ocean-700)]" />
              </div>
              <div>
                <h2
                  className="font-heading text-xl sm:text-2xl font-semibold leading-tight"
                  data-testid="verify-title"
                >
                  {verifyStep === 'contact'
                    ? 'Start Your Quote — Where should we send your results?'
                    : 'Enter the code we just texted you'}
                </h2>
                <p className="text-xs sm:text-sm text-[var(--brand-slate-600)] mt-1">
                  {verifyStep === 'contact'
                    ? 'We’ll text you a secure link with your pricing and options. No spam. No pressure.'
                    : `Sent to ${phoneE164 || formatPhone(phone)}. Standard SMS rates apply.`}
                </p>
              </div>
            </div>

            {verifyStep === 'contact' ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                <p
                  className="text-xs text-[var(--brand-slate-500)] -mb-1"
                  data-testid="verify-helper"
                >
                  Most quotes only require 2–4 photos.
                </p>
                <div>
                  <Label htmlFor="vname" className="text-sm font-medium">
                    First name
                  </Label>
                  <Input
                    id="vname"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Maria"
                    autoComplete="given-name"
                    className="mt-1.5 h-12 text-base"
                    data-testid="verify-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vphone" className="text-sm font-medium">
                    Mobile number
                  </Label>
                  <Input
                    id="vphone"
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="(305) 555-1234"
                    autoComplete="tel"
                    className="mt-1.5 h-12 text-base tracking-wide"
                    data-testid="verify-phone-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full h-12 sm:h-14 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                  data-testid="verify-send-button"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending code…
                    </>
                  ) : (
                    <>
                      Text me a verification code
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <p
                  className="text-center text-sm font-medium text-[var(--brand-slate-800)] pt-1"
                  data-testid="momentum-line"
                >
                  Most quotes are delivered within 24–48 hours.
                </p>

                <div
                  className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-0.5 text-xs text-[var(--brand-slate-600)]"
                  data-testid="cta-microcopy"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                    No appointment
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                    No pressure
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                    Fast turnaround
                  </span>
                </div>

                <p className="text-[11px] text-[var(--brand-slate-500)] text-center pt-1 flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3" />
                  We text once for verification, then email your quote. We never sell your info.
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <Label htmlFor="vcode" className="text-sm font-medium">
                    6-digit code
                  </Label>
                  <Input
                    id="vcode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="mt-1.5 h-14 text-center text-2xl tracking-[0.5em] font-semibold"
                    data-testid="verify-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={verifying}
                  className="w-full h-12 sm:h-14 rounded-xl text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                  data-testid="verify-confirm-button"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    <>
                      Verify & continue
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setVerifyStep('contact');
                      setCode('');
                    }}
                    className="text-[var(--brand-slate-600)] hover:text-[var(--brand-slate-900)] underline-offset-2 hover:underline"
                    data-testid="verify-change-number"
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || sending}
                    onClick={handleResend}
                    className="inline-flex items-center gap-1 text-[var(--brand-ocean-700)] disabled:opacity-50 hover:underline underline-offset-2"
                    data-testid="verify-resend"
                  >
                    <RefreshCw className="h-3 w-3" />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
